// SPDX-License-Identifier: GPL-3.0-or-later
// Entry point of the engine. The client calls `createEngine` with a table spec,
// the rules of the table and the options. The engine has no DOM, no three and no clock.
//
// Conventions that the table data and the rules must follow:
//
// - Table axes. x grows to the right. y grows down the table, toward the flippers and the drain.
//   z grows up from the playfield.
// - Kick angle. `RulesContext.kick(kicker, angleDeg, speed)` and `KickerSpec.ejectAngle` use
//   degrees. 0 sends the ball up the table (toward smaller y). 90 sends it to the right.
//   180 sends it down the table. 270 sends it to the left. The ball velocity is
//   (sin(angle) * speed, -cos(angle) * speed). `src/table/data` uses the same convention.
// - Flipper angle. A spec angle is in degrees from the +x axis and grows clockwise on the screen.
//   30 points right and down. -25 points right and up. The engine turns it into the upstream
//   angle (0 points up) with `SPEC_TO_UPSTREAM_ANGLE_DEG`. The `angle` of a flipper item frame
//   uses the spec convention again, in radians.
// - Kicker hold. A kicker that is not a drain and not fall-through holds its ball.
//   The ball stays frozen until the rules call `kick()`. A kick on a kicker with no ball does nothing.
// - Drain. A kicker with role "drain" removes the ball and sends a "drain" event.
// - Player input. A flipper button press reaches the rules as a "hit" event with ball id 0.
//   Its kind is "flipper" and its item is the flipper name. A nudge during the nudge cooldown
//   reaches the rules as a "hit" event with ball id 0, kind "decoration" and item "Tilt".
//   The engine sends no event for a release.
// - Score. `addScore` adds the points as given. The engine does not scale them by the multiplier.
//   `endMode` reports its bonus in the `mode_end` event and does not add it to the score.
//   The bonus that `onBallEnd` returns is added to the score once.
// - End of a ball. `endBall(reason)` drains every ball in play and ends the ball with no bonus.
//   Ball save does not apply to it.

import type {
  CreateEngine,
  EngineOptions,
  PinballEngine,
  TableRules,
  TableSpec,
} from "../shared/contracts";
import { Game } from "./core/game";

/**
 * Builds an engine for one table. The engine plays one game at a time.
 *
 * @throws Error when two items of the table share a name.
 * @throws RangeError when an option is out of range.
 */
export const createEngine: CreateEngine = (
  table: TableSpec,
  rules: TableRules,
  opts: EngineOptions,
): PinballEngine => new Game(table, rules, opts);

export { Rng } from "./core/rng";
